import { useEffect, useState, type ComponentType } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { DemoUser, User } from './auth';
import { demoUsers, fetchMe, login, logout } from './auth';
import { screensFor, type ScreenKey } from './nav';
import { getNavCounts } from './nav-api';
import { getTheme, toggleTheme } from './theme';
import { UserContext } from './user-context';
import { useData } from './useData';
import {
  CatalogScreen,
  DashScreen,
  DealsScreen,
  KppScreen,
  PayScreen,
  PlanScreen,
  ProdScreen,
  SettingsScreen,
  ShipScreen,
  SupplyScreen,
} from './screens';

const SCREEN_COMP: Record<ScreenKey, ComponentType> = {
  dash: DashScreen,
  deals: DealsScreen,
  catalog: CatalogScreen,
  pay: PayScreen,
  plan: PlanScreen,
  prod: ProdScreen,
  supply: SupplyScreen,
  ship: ShipScreen,
  kpp: KppScreen,
  settings: SettingsScreen,
};

export function Shell({ user, onUser }: { user: User; onUser: (u: User | null) => void }) {
  // Пункт «Настройки» показываем, если у роли есть хотя бы один раздел (cfg_*).
  const hasSettings = user.screens.some((s) => s.startsWith('cfg_'));
  const allowed = screensFor(hasSettings ? [...user.screens, 'settings'] : user.screens);
  const home = allowed[0]?.path ?? '/dash';
  const [theme, setTheme] = useState(getTheme());
  const [navOpen, setNavOpen] = useState(false);
  const [demos, setDemos] = useState<{ password: string; users: DemoUser[] }>({
    password: '',
    users: [],
  });
  const loc = useLocation();
  // Счётчики бейджей — самый частый запрос (Shell смонтирован всегда), поэтому реже:
  // они и так обновляются при каждой навигации (useEffect по loc.pathname ниже).
  const { data: navData, refresh: refreshCounts } = useData('nav', getNavCounts, 120000);
  const counts = navData?.counts ?? {};
  const alert = navData?.alert ?? {};

  useEffect(() => {
    demoUsers()
      .then(setDemos)
      .catch(() => {});
  }, []);
  useEffect(() => {
    refreshCounts();
  }, [loc.pathname, refreshCounts]);

  // Перепроверка учётки при навигации: смена роли/доступов применяется без
  // перелогина, а отключённая учётка мгновенно выкидывает на вход.
  useEffect(() => {
    let cancelled = false;
    fetchMe().then((r) => {
      if (cancelled) return;
      if (r.status === 'invalid') {
        onUser(null); // учётку отключили/удалили — на вход
        return;
      }
      if (r.status !== 'ok') return; // сетевой сбой — сессию не трогаем
      const fresh = r.user;
      const changed =
        fresh.role !== user.role ||
        [...fresh.screens].sort().join(',') !== [...user.screens].sort().join(',');
      if (changed) onUser(fresh);
    });
    return () => {
      cancelled = true;
    };
    // намеренно перепроверяем только при смене пути
  }, [loc.pathname]);

  async function switchRole(email: string) {
    if (!email) return;
    const u = await login(email, demos.password || 'demo1234');
    onUser(u);
  }

  return (
    <div className="app">
      <header className="apphead">
        <button className="icon-btn burger" title="Меню" onClick={() => setNavOpen((v) => !v)}>
          ☰
        </button>
        <div className="brand">
          <span className="logo">A</span>
          <div>
            <div className="bt">
              AZAM <span className="mut2">· Пульт завода</span>
            </div>
            <div className="bsub">элеваторное оборудование · единая система</div>
          </div>
        </div>
        <span className="spacer" />
        {navData?.onec ? (
          <span className="chip grn">● 1С · подключён</span>
        ) : (
          <span className="chip" title="1С не подключён — настройте в разделе «Настройки»">
            ● 1С · не подключён
          </span>
        )}
        {demos.users.length > 0 && (
          <select
            className="rolesel"
            value={demos.users.find((d) => d.role === user.role)?.email ?? ''}
            onChange={(e) => switchRole(e.target.value)}
            title="Сменить роль (демо)"
          >
            {!demos.users.some((d) => d.role === user.role) && (
              <option value="">{user.name}</option>
            )}
            {demos.users.map((d) => (
              <option key={d.email} value={d.email}>
                {d.ico} {d.role_name}
              </option>
            ))}
          </select>
        )}
        <button className="icon-btn" title="Тема" onClick={() => setTheme(toggleTheme())}>
          {theme === 'dark' ? '☀️' : '🌙'}
        </button>
        <button
          className="btn ghost sm"
          onClick={async () => {
            await logout();
            onUser(null);
          }}
        >
          Выйти
        </button>
      </header>

      <nav className={'topnav' + (navOpen ? ' open' : '')}>
        {allowed.map((s) => {
          const n = counts[s.key];
          return (
            <NavLink
              key={s.key}
              to={s.path}
              onClick={() => setNavOpen(false)}
              className={({ isActive }) => 'tnav' + (isActive ? ' on' : '')}
            >
              <span className="ni-ico">{s.ico}</span>
              <span>{s.name}</span>
              {n ? <span className={'badge' + (alert[s.key] ? ' red' : '')}>{n}</span> : null}
            </NavLink>
          );
        })}
      </nav>

      <section className="content">
        <UserContext.Provider value={user}>
          <Routes>
            {allowed.map((s) => {
              const Comp = SCREEN_COMP[s.key];
              return <Route key={s.key} path={s.path} element={<Comp />} />;
            })}
            <Route path="*" element={<Navigate to={home} replace />} />
          </Routes>
        </UserContext.Provider>
      </section>
    </div>
  );
}
